import { useState } from "react";
import "../../../styles/pages/parameter-mapping.css";
import { Link2 } from "lucide-react";
import { useEffect } from "react";
import Toast from "../../../shared/components/Toast";
import apiClient from "../../../shared/API/apiClient";

// const activities = [
//   { id: 1, name: "Effluent Monitoring" },
//   { id: 2, name: "Air Monitoring" },
// ];

// const dummyParameters = [
//   { id: 1, name: "COD", code: "COD", unit: "mg/l" },
//   { id: 2, name: "BOD", code: "BOD", unit: "mg/l" },
//   { id: 3, name: "TSS", code: "TSS", unit: "mg/l" },
//   { id: 4, name: "pH", code: "PH", unit: "unit" },
//   { id: 5, name: "Ammonia", code: "NH3", unit: "mg/l" },
// ];

export default function ParameterMapping() {
  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState("");
  const [parameters, setParameters] = useState([]);
  const [selectedParams, setSelectedParams] = useState([]);
  const [loading, setLoading] = useState(false);

  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  // Activity dropdown API

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const res = await apiClient.get("/api/getactivity-by-monitoring");

        if (res.data.code === 200) {
          setActivities(res.data.data || []);

          // default select first activity
          if (res.data.data.length > 0) {
            setSelectedActivity(res.data.data[0].id);
          }
        }
      } catch (err) {
        console.error("Activity API error:", err);
      }
    };

    fetchActivities();
  }, []);

  //Parameters API call

  useEffect(() => {
    const fetchParams = async () => {
      if (!selectedActivity) return;

      setLoading(true);
      try {
        const res = await apiClient.get("/api/activity-parameters", {
          params: {
            activity_type_id: selectedActivity,
          },
        });

        if (res.data.code === 200) {
          const data = res.data.data || [];

          setParameters(data);

          // pre-selected params from API
          const selected = data
            .filter((p) => p.is_selected === 1)
            .map((p) => p.id);

          setSelectedParams(selected);
        }
      } catch (err) {
        console.error("Params API error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchParams();
  }, [selectedActivity]);

  const handleSelectAll = () => {
    if (selectedParams.length === parameters.length) {
      setSelectedParams([]);
    } else {
      setSelectedParams(parameters.map((p) => p.id));
    }
  };

  const handleCheckbox = (id) => {
    if (selectedParams.includes(id)) {
      setSelectedParams(selectedParams.filter((p) => p !== id));
    } else {
      setSelectedParams([...selectedParams, id]);
    }
  };

  //Handle save

  const handleSave = async () => {
    if (!selectedActivity) {
      setToast({ message: "Please select an activity", type: "error" });
      return;
    }

    setSaving(true);

    try {
      const payload = {
        activity_type_id: Number(selectedActivity),
        parameter_ids: selectedParams,
      };

      const res = await apiClient.post("/api/activity-parameters/save", payload);

      if (res.data.code === 200) {
        setToast({
          message: "Parameters mapped successfully",
          type: "success",
        });
      } else {
        setToast({
          message: "Something went wrong",
          type: "error",
        });
      }
    } catch (err) {
      console.error("Save API error:", err);

      setToast({
        message: err?.response?.data?.message || "Failed to save mapping",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  //Handle Reset Button
  const handleReset = () => {
    const defaultSelected = parameters
      .filter((p) => p.is_selected === 1)
      .map((p) => p.id);

    setSelectedParams(defaultSelected);
  };

  return (
    <div className="dash-page">
      <div className="le-page-header">
        <div>
          <div className="dash-page-subtitle">
            Configuration / Parameter Mapping
          </div>
          <div className="dash-page-title">Parameter Mapping</div>
          <div className="dash-page-subtitle">
            Link environmental parameters to the activities where they are
            tracked.
          </div>
        </div>
      </div>

      {/* Activity Card */}
      <div className="pm-card">
        <div className="ep-card-header">
          <Link2 className="ep-card-icon" />
          <span className="ep-card-title">Activity</span>
        </div>

        <p className="pm-subtext">
          Choose the monitoring activity you want to configure parameters for.
        </p>

        <label className="pm-label">Select Activity</label>
        <select
          className="pm-select"
          value={selectedActivity}
          onChange={(e) => setSelectedActivity(e.target.value)}
        >
          {activities.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </div>

      {/* Parameters Card */}
      <div className="pm-card pm-params-card">
        <div className="pm-header">
          <div>
            <span className="ep-card-title">Select Parameters</span>
            <p className="pm-subtext">
              Choose which parameters apply to <b>{selectedActivity}</b>.
            </p>
          </div>

          <div className="pm-badge">
            {selectedParams.length} of {parameters.length} selected
          </div>
        </div>

        <div className="pm-divider"></div>

        {/* Select All */}
        <div className="pm-row pm-select-all">
          <div className="pm-left">
            <input
              type="checkbox"
              checked={selectedParams.length === parameters.length}
              onChange={handleSelectAll}
            />
            <span>Select all parameters</span>
          </div>
        </div>

        {/* Parameter List */}

        {loading ? (
          <div className="table-loader-wrap-full">
            <div className="table-loader-wrap-full">
              <div className="table-loader"></div>
              <p className="table-loader-text">Parameters loading...</p>
            </div>
          </div>
        ) : parameters.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">
                    No data available
                  </td>
                </tr>
              ) : (
          parameters.map((param) => (
            <div key={param.id} className="pm-row">
              <div className="pm-left">
                <input
                  type="checkbox"
                  checked={selectedParams.includes(param.id)}
                  onChange={() => handleCheckbox(param.id)}
                />
                <span className="pm-name">{param.name}</span>
              </div>

              <div className="pm-right">
                <span className="pm-chip">{param.code}</span>
                <span className="pm-unit">{param.unit_id}</span>
              </div>
            </div>
          ))
        )}

        {/* Footer */}
        <div className="pm-footer">
          <button className="pm-btn-outline" onClick={handleReset}>
            Reset
          </button>
          <button
            className="org-btn-save"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Mapping"}
          </button>
        </div>
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
